/**
 * Fase 5 — Kurs-veiviser steg 1: Grunninfo.
 * Prøvesett, medlemsorganisasjon (skjult for member), tittel med maler, beskrivelse.
 */
import { useEffect, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { authenticatedFetch, getUser } from '../../../lib/auth';
import type { CourseWizardData } from '../NewCourseInstanceWizard';

interface ExamSetOption {
  id: string;
  title: string;
  description?: string;
  study_plan_title?: string;
  study_plan_description?: string;
}

interface MemberOption {
  id: string;
  organization_name: string;
}

export interface CourseBasicsStepProps {
  data: CourseWizardData;
  patch: (updates: Partial<CourseWizardData>) => void;
  /** Hvis true, hopp over prøvesett-velger (allerede valgt fra forrige veiviser) */
  skipExamSetPicker: boolean;
}

export default function CourseBasicsStep({ data, patch, skipExamSetPicker }: CourseBasicsStepProps) {
  const [examSets, setExamSets] = useState<ExamSetOption[]>([]);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Last brukerens kontekst
  useEffect(() => {
    (async () => {
      try {
        const u = await getUser();
        if (u) {
          setUserRole(u.role);
          // Member/member-admin: automatisk sett egen org
          if (u.role === 'member' && u.member_id) {
            patch({ member_id: u.member_id });
          }
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Last prøvesett og medlemsorganisasjoner (hvis admin/systemadmin)
  useEffect(() => {
    if (skipExamSetPicker) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const examRes = await authenticatedFetch('/api/exams/list');
        const examJson = await examRes.json();
        if (examJson.success && Array.isArray(examJson.exam_sets)) {
          setExamSets(examJson.exam_sets);
        } else if (Array.isArray(examJson)) {
          setExamSets(examJson);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Kunne ikke laste prøvesett');
      } finally {
        setLoading(false);
      }
    })();
  }, [skipExamSetPicker]);

  useEffect(() => {
    if (!userRole || userRole === 'member') return;
    (async () => {
      try {
        const res = await authenticatedFetch('/api/admin/members');
        const contentType = res.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) return;
        const json = await res.json();
        if (json.success && Array.isArray(json.members)) setMembers(json.members);
        else if (Array.isArray(json)) setMembers(json);
      } catch { /* ignore */ }
    })();
  }, [userRole]);

  // Tittel-maler basert på studieplan (faget), ikke prøvesettets navn.
  // Prøvesettet er den gjenbrukbare prøven; kursinstansen er en kjøring av faget med
  // tidspunkt/sted/kull. Derfor tar vi utgangspunkt i studieplanens tittel.
  const selectedExam = examSets.find((e) => e.id === data.exam_set_id);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const season = currentMonth < 6 ? 'vår' : 'høst';
  // Fag-navn: foretrekk studieplanens tittel, fall tilbake til exam-set-tittelen
  // kun hvis studieplan-tittel mangler.
  const courseFagName = selectedExam?.study_plan_title || selectedExam?.title || '';
  const titleTemplates = courseFagName
    ? [
        `${courseFagName} — ${season} ${currentYear}`,
        `${courseFagName} ${currentYear}`,
        `${courseFagName} — ${new Date().toLocaleDateString('nb-NO', { month: 'long', year: 'numeric' })}`,
      ]
    : [];

  const showMemberPicker = userRole && userRole !== 'member' && !skipExamSetPicker;

  return (
    <div className="space-y-5">
      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Prøvesett */}
      {!skipExamSetPicker ? (
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-1">
            Prøvesett <span className="text-red-500">*</span>
          </label>
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-kf-gray-600">
              <Loader2 className="w-4 h-4 animate-spin" /> Laster prøvesett…
            </div>
          ) : (
            <select
              value={data.exam_set_id}
              onChange={(e) => patch({ exam_set_id: e.target.value })}
              className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
            >
              <option value="">— Velg prøvesett —</option>
              {examSets.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title}
                  {e.study_plan_title ? ` (${e.study_plan_title})` : ''}
                </option>
              ))}
            </select>
          )}
          {examSets.length === 0 && !loading && (
            <p className="text-xs text-kf-gray-500 mt-1">
              Ingen prøvesett funnet. Opprett et prøvesett først via prøvesett-veiviseren.
            </p>
          )}
          {/* Foreslå tittel + beskrivelse — vises rett under prøvesett når ett er valgt */}
          {selectedExam && (
            <button
              type="button"
              onClick={() => {
                // Utled både kursnavn og beskrivelse fra studieplan + prøvesett.
                // Ingen LLM-kall — vi bruker etablert fagtekst som grunnlag;
                // brukeren justerer selv deretter. Tittel settes BARE hvis tom
                // (så brukeren ikke mister innhold hun selv har skrevet).
                const patchObj: { name?: string; description?: string } = {};
                if (!data.name && courseFagName) {
                  patchObj.name = `${courseFagName} — ${season} ${currentYear}`;
                }
                const parts: string[] = [];
                if (selectedExam.study_plan_description) {
                  parts.push(selectedExam.study_plan_description);
                } else if (selectedExam.description) {
                  parts.push(selectedExam.description);
                }
                parts.push(
                  `Kurset avsluttes med «${selectedExam.title}». ` +
                  `Kandidater som består får utstedt kompetanseattest.`,
                );
                patchObj.description = parts.join('\n\n').trim();
                patch(patchObj);
              }}
              className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100 font-medium"
              title="Foreslår kursnavn (hvis tomt) og beskrivelse basert på studieplan og prøvesett"
            >
              <Sparkles className="w-3.5 h-3.5" />
              Foreslå tittel og beskrivelse
            </button>
          )}
        </div>
      ) : (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-sm text-emerald-900">
          Prøvesett valgt fra forrige veiviser. Fortsett med kurs-detaljer.
        </div>
      )}

      {/* Medlemsorganisasjon — kun for admin/systemadmin */}
      {showMemberPicker && (
        <div>
          <label className="block text-sm font-medium text-kf-gray-700 mb-1">
            Medlemsorganisasjon <span className="text-red-500">*</span>
          </label>
          <select
            value={data.member_id}
            onChange={(e) => patch({ member_id: e.target.value })}
            className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
          >
            <option value="">— Velg medlemsorganisasjon —</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.organization_name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tittel med forslag */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">
          Kursnavn <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={data.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder={courseFagName ? `F.eks: ${courseFagName} — ${season} ${currentYear}` : 'F.eks: Ledelseskurs vår 2026'}
          className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
        {titleTemplates.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            <span className="text-xs text-kf-gray-500">Forslag:</span>
            {titleTemplates.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => patch({ name: t })}
                className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded border border-kf-blue-200 bg-kf-blue-50 text-kf-blue-700 hover:bg-kf-blue-100"
              >
                <Sparkles className="w-3 h-3" />
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Beskrivelse */}
      <div>
        <label className="block text-sm font-medium text-kf-gray-700 mb-1">Beskrivelse</label>
        <textarea
          value={data.description}
          onChange={(e) => patch({ description: e.target.value })}
          rows={3}
          placeholder="Kort beskrivelse av kurset — vises for kandidater og evt. på kompetanseheving.no…"
          className="w-full px-3 py-2 border border-kf-gray-300 rounded-lg focus:ring-2 focus:ring-kf-blue-500 focus:border-kf-blue-500"
        />
      </div>
    </div>
  );
}
